import { useEffect, useRef, useState } from "react";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { Show } from "shared/components/helpers/Show";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Modal } from "shared/components/Modal";
import { Pagination } from "shared/components/Pagination";
import {
  Attachment01,
  DownloadCloud01,
  ChevronLeft,
  ChevronRight
} from "untitledui-js-base";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Document, Page } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { cx } from "shared/utils";

const options = { cMapUrl: "/cmaps/" };

export const AssetViewer = ({
  src,
  open,
  onClose,
  mimeType,
  filename,
  fileSize,
  currentIndex,
  totalItems,
  onPrevious,
  onNext
}: {
  src: string;
  open: boolean;
  onClose: () => void;
  mimeType: string;
  filename: string;
  fileSize: number;
  currentIndex?: number;
  totalItems?: number;
  onPrevious?: () => void;
  onNext?: () => void;
}) => {
  const isPdf = mimeType.includes("pdf");
  const isImage = mimeType.includes("image");
  const [numPages, setNumPages] = useState<number>();
  const [page, setPage] = useState(1);
  const [documentWidth, setDocumentWidth] = useState(1024);
  const containerRef = useRef<HTMLDivElement>(null);

  const [transitioning, setTransitioning] = useState(false);
  const [slideDirection, setSlideDirection] = useState<"left" | "right" | null>(
    null
  );
  const prevIndexRef = useRef(currentIndex);

  useEffect(() => {
    if (prevIndexRef.current !== currentIndex) {
      setTransitioning(true);
      const timeout = setTimeout(() => {
        setTransitioning(false);
        setSlideDirection(null);
      }, 300);
      prevIndexRef.current = currentIndex;
      return () => clearTimeout(timeout);
    }
  }, [currentIndex]);

  const handlePrevious = () => {
    if (onPrevious && !transitioning) {
      setSlideDirection("right");
      onPrevious();
    }
  };

  const handleNext = () => {
    if (onNext && !transitioning) {
      setSlideDirection("left");
      onNext();
    }
  };

  const getTransitionClasses = () => {
    if (!transitioning) return "opacity-100 translate-x-0";

    if (slideDirection === "left") return "opacity-0 -translate-x-8";

    if (slideDirection === "right") return "opacity-0 translate-x-8";

    return "opacity-0 translate-x-0";
  };

  const hasNavigation =
    currentIndex !== undefined && totalItems !== undefined && totalItems > 1;

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const screenWidth = window.innerWidth;
      setDocumentWidth(screenWidth > 1024 ? 768 : screenWidth - 40);
    });
    resizeObserver.observe(document.body);
    return () => resizeObserver.disconnect();
  }, []);

  function onDocumentLoadSuccess({ numPages: nextNumPages }: PDFDocumentProxy) {
    setNumPages(nextNumPages);
  }

  if (!open) return null;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      backdropClassName="bg-alpha-200"
      panelClassName="bg-white"
    >
      <div>
        <div className="border-b border-alpha-100 flex gap-2 p-6">
          <Show if={isPdf && Boolean(numPages) && (numPages || 0) > 1}>
            <Pagination
              total={numPages || 0}
              current={page}
              onChange={setPage}
            />
          </Show>

          <a
            download
            className="block ml-auto"
            href={src}
            target="_blank"
            rel="noreferrer"
          >
            <Button
              Icon={DownloadCloud01}
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              borderLess
            >
              Download
            </Button>
          </a>
          <IconButton
            HugeIcon={Cancel01Icon}
            variant={IconButtonVariant.TEXT_BASE}
            size={IconButtonSize.SM}
            onClick={onClose}
          />
        </div>
        <div
          className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto relative"
          ref={containerRef}
        >
          <Show if={isPdf}>
            <Document
              key={`pdf_${src}_${page}`}
              file={src}
              onLoadSuccess={onDocumentLoadSuccess}
              options={options}
            >
              <Page
                className="border border-alpha-100"
                key={`page_${page}`}
                pageNumber={page}
                width={documentWidth}
              />
            </Document>
          </Show>

          <Show if={!isPdf && isImage}>
            <div
              className={cx(
                "transition-all duration-300",
                "max-w-full mx-auto block",
                "border border-alpha-100",
                getTransitionClasses()
              )}
            >
              <img src={src} alt="attachment" className="w-full h-auto" />
            </div>
          </Show>

          <Show if={!isPdf && !isImage}>
            <div className="border border-alpha-100 rounded bg-white flex gap-2 items-center p-2 max-w-[240px]">
              <Attachment01 size="16" className="stroke-content-100 shrink-0" />
              <div>
                <p className="text-sm font-medium text-content-400 truncate max-w-[140px]">
                  {filename}
                </p>
                <p className="text-xs text-content-200">
                  {mimeType} - {fileSize}KB
                </p>
              </div>
            </div>
          </Show>

          <Show if={hasNavigation && isImage}>
            {/* Photo Navigation Counter */}
            <div className="flex items-center justify-end gap-4 mt-4">
              <Show if={hasNavigation}>
                <div className="flex items-center gap-2 text-sm text-content-400">
                  <span>
                    {(currentIndex || 0) + 1} / {totalItems}
                  </span>
                </div>
              </Show>

              {/* Navigation Controls for Images */}
              <div className="flex items-center gap-2">
                <IconButton
                  Icon={ChevronLeft}
                  variant={IconButtonVariant.BASE}
                  onClick={handlePrevious}
                  disabled={currentIndex === 0 || transitioning}
                />

                <IconButton
                  Icon={ChevronRight}
                  variant={IconButtonVariant.BASE}
                  onClick={handleNext}
                  disabled={
                    currentIndex === (totalItems || 0) - 1 || transitioning
                  }
                />
              </div>
            </div>
          </Show>
        </div>
      </div>
    </Modal>
  );
};
